<script lang="ts" setup>
import { ref, watch, computed } from 'vue';
import { useModalStore } from '@/store/modal.store';
import { useAuthStore } from '@/store/auth.store';
import { useSubscribersStore } from '@/store/subscribers.store';
import { useBillingStore } from '@/store/billing.store';
import DefaultBtn from "@/components/block/DefaultBtn.vue";

const modalStore = useModalStore();
const authStore = useAuthStore();
const subscriberStore = useSubscribersStore();
const billingStore = useBillingStore();

const props = defineProps<{
	closeSignal?: boolean
}>();

watch(() => props.closeSignal, () => {
	if (props.closeSignal) {
		close();
	}
});

const loading = ref<boolean>(false);

// Form data
const formData = ref({
	deviceIMEI: '',
	deviceName: '',
	notifications_name_1: '',
	notifications_phone_1: '',
	notifications_email_1: '',
	notifications_name_2: '',
	notifications_phone_2: '',
	notifications_email_2: '',
	notifications_name_3: '',
	notifications_phone_3: '',
	notifications_email_3: ''
});

const showContact2 = ref(false);
const showContact3 = ref(false);

// Prefill notification info from account
const prefilledNotificationInfo = computed(() => {
	const subscriber = authStore.subscriber;
	return {
		name: `${subscriber.name || ''} ${subscriber.lastName || ''}`.trim() || '',
		phone: subscriber.phone || '',
		email: subscriber.email || ''
	};
});

// Initialize form with prefilled data
const initializeForm = () => {
	formData.value.notifications_name_1 = prefilledNotificationInfo.value.name;
	formData.value.notifications_phone_1 = prefilledNotificationInfo.value.phone;
	formData.value.notifications_email_1 = prefilledNotificationInfo.value.email;
};

// Initialize form when component mounts
initializeForm();

const validateIMEI = (imei: string): boolean => {
	// Basic IMEI validation - should be 15 digits
	return /^\d{15}$/.test(imei);
};

const validateForm = (): boolean => {
	if (!formData.value.deviceIMEI || !validateIMEI(formData.value.deviceIMEI)) {
		alert('Please enter a valid 15-digit IMEI number');
		return false;
	}
	
	if (!formData.value.deviceName.trim()) {
		alert('Please enter a device name');
		return false;
	}
	
	if (!formData.value.notifications_name_1.trim()) {
		alert('Please enter a contact name');
		return false;
	}
	
	if (!formData.value.notifications_phone_1.trim()) {
		alert('Please enter a contact phone number');
		return false;
	}
	
	if (!formData.value.notifications_email_1.trim()) {
		alert('Please enter a contact email address');
		return false;
	}
	
	return true;
};

const handleSubmit = async (): Promise<void> => {
	if (!validateForm()) return;
	
	loading.value = true;
	
	try {
		// Create new device
		const newDevice = await subscriberStore.addNewDevice({
			deviceIMEI: formData.value.deviceIMEI,
			main_name: formData.value.deviceName,
			notifications_name_1: formData.value.notifications_name_1,
			notifications_phone_1: formData.value.notifications_phone_1,
			notifications_email_1: formData.value.notifications_email_1,
			notifications_name_2: formData.value.notifications_name_2 || undefined,
			notifications_phone_2: formData.value.notifications_phone_2 || undefined,
			notifications_email_2: formData.value.notifications_email_2 || undefined,
			notifications_name_3: formData.value.notifications_name_3 || undefined,
			notifications_phone_3: formData.value.notifications_phone_3 || undefined,
			notifications_email_3: formData.value.notifications_email_3 || undefined,
			deviceType: 'single'
		});

		modalStore.closeModal();

		if (authStore.isRetailSubscriber) {
			const subscribeNow = window.confirm(
				'Device added successfully. Subscribe for $5/month to activate monitoring and alerts for this device?'
			);
			if (subscribeNow) {
				await billingStore.subscribeToDevice(newDevice.id);
			}
		}
	} catch (error) {
		console.error('Failed to add new device:', error);
		alert('Failed to add new device. Please try again.');
	} finally {
		loading.value = false;
	}
};

const close = (): void => {
	if (!loading.value) {
		modalStore.closeModal();
	}
};
</script>

<template>
	<div class="modal">
		<div class="modal__body">
			<div class="modal__content">
				<h3 class="modal__title">Add New Device</h3>
				<p class="modal__text">
					Add a new device to your account. Notification information has been prefilled from your account.
				</p>
				
				<form @submit.prevent="handleSubmit" class="modal__form">
					<div class="modal__input-group">
						<label for="deviceImei">Device IMEI *</label>
						<input
							v-model="formData.deviceIMEI"
							id="deviceImei"
							type="text"
							placeholder="Enter 15-digit IMEI number"
							class="modal__input"
							:disabled="loading"
							required
						/>
						<small class="modal__help-text">Enter the 15-digit IMEI number found on your device</small>
					</div>
					
					<div class="modal__input-group">
						<label for="deviceName">Device Name *</label>
						<input
							v-model="formData.deviceName"
							id="deviceName"
							type="text"
							placeholder="Enter device name"
							class="modal__input"
							:disabled="loading"
							required
						/>
					</div>
					
					<div class="modal__section">
						<h4 class="modal__section-title">Notification Contact 1 *</h4>
						
						<div class="modal__input-group">
							<label for="contactName">Contact Name *</label>
							<input
								v-model="formData.notifications_name_1"
								id="contactName"
								type="text"
								placeholder="Enter contact name"
								class="modal__input"
								:disabled="loading"
								required
							/>
						</div>
						
						<div class="modal__input-group">
							<label for="contactPhone">Phone Number *</label>
							<input
								v-model="formData.notifications_phone_1"
								id="contactPhone"
								type="tel"
								placeholder="Enter phone number"
								class="modal__input"
								:disabled="loading"
								required
							/>
						</div>
						
						<div class="modal__input-group">
							<label for="contactEmail">Email Address *</label>
							<input
								v-model="formData.notifications_email_1"
								id="contactEmail"
								type="email"
								placeholder="Enter email address"
								class="modal__input"
								:disabled="loading"
								required
							/>
						</div>
						<button v-if="!showContact2" type="button" class="modal__add-contact" @click="showContact2 = true">
							<span class="material-symbols-outlined">add</span>
							Add Contact 2
						</button>
					</div>

					<div v-if="showContact2" class="modal__section">
						<h4 class="modal__section-title">Notification Contact 2 (Optional)</h4>
						<div class="modal__input-group">
							<label for="contactName2">Name</label>
							<input v-model="formData.notifications_name_2" id="contactName2" type="text" placeholder="Enter contact name" class="modal__input" :disabled="loading" />
						</div>
						<div class="modal__input-group">
							<label for="contactPhone2">Phone Number</label>
							<input v-model="formData.notifications_phone_2" id="contactPhone2" type="tel" placeholder="Enter phone number" class="modal__input" :disabled="loading" />
						</div>
						<div class="modal__input-group">
							<label for="contactEmail2">Email Address</label>
							<input v-model="formData.notifications_email_2" id="contactEmail2" type="email" placeholder="Enter email address" class="modal__input" :disabled="loading" />
						</div>
						<button v-if="!showContact3" type="button" class="modal__add-contact" @click="showContact3 = true">
							<span class="material-symbols-outlined">add</span>
							Add Contact 3
						</button>
					</div>

					<div v-if="showContact3" class="modal__section">
						<h4 class="modal__section-title">Notification Contact 3 (Optional)</h4>
						<div class="modal__input-group">
							<label for="contactName3">Name</label>
							<input v-model="formData.notifications_name_3" id="contactName3" type="text" placeholder="Enter contact name" class="modal__input" :disabled="loading" />
						</div>
						<div class="modal__input-group">
							<label for="contactPhone3">Phone Number</label>
							<input v-model="formData.notifications_phone_3" id="contactPhone3" type="tel" placeholder="Enter phone number" class="modal__input" :disabled="loading" />
						</div>
						<div class="modal__input-group">
							<label for="contactEmail3">Email Address</label>
							<input v-model="formData.notifications_email_3" id="contactEmail3" type="email" placeholder="Enter email address" class="modal__input" :disabled="loading" />
						</div>
					</div>
					
					<div class="modal__btn">
						<DefaultBtn 
							:text="'Cancel'" 
							:type="'button'" 
							:class="'btn--white'"
							@click="close"
							:disabled="loading"
						/>
						<DefaultBtn 
							:text="'Add Device'" 
							:type="'submit'" 
							:class="'btn--primary'"
							:loading="loading"
						/>
					</div>
				</form>
			</div>
		</div>
	</div>
</template>

<style scoped>

.modal__content {
	padding: 32px;
}

.modal__title {
	font-size: 22px;
	font-weight: 700;
	margin-bottom: 16px;
	color: #0F172A;
	text-align: center;
}

.modal__text {
	font-size: 16px;
	line-height: 1.6;
	margin-bottom: 32px;
	color: #64748B;
	text-align: center;
}

.modal__form {
	display: flex;
	flex-direction: column;
	gap: 25px;
}

.modal__section {
	background: #F8FAFC;
	padding: 24px;
	border-radius: 10px;
	border: 1px solid #E2E8F0;
}

.modal__section-title {
	font-size: 18px;
	font-weight: 700;
	color: #0F172A;
	margin-bottom: 20px;
	border-bottom: 1px solid #E2E8F0;
	padding-bottom: 12px;
}

.modal__add-contact {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 16px;
	padding: 10px 16px;
	background: transparent;
	border: 1px dashed #2563EB;
	border-radius: 8px;
	color: #2563EB;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;
}

.modal__add-contact:hover {
	background: rgba(37, 99, 235, 0.08);
}

.modal__add-contact .material-symbols-outlined {
	font-size: 20px;
}

.modal__input-group {
	position: relative;
	margin-bottom: 20px;
}

.modal__input-group:last-child {
	margin-bottom: 0;
}

.modal__input-group label {
	display: block;
	color: #64748B;
	font-weight: 600;
	font-size: 14px;
	margin-bottom: 8px;
}

.modal__input {
	height: 55px;
	width: 100%;
	padding: 0 15px;
	border: 1px solid #E2E8F0;
	border-radius: 10px;
	font-size: 16px;
	transition: all 0.2s ease;
	background: #fff;
}

.modal__input:focus {
	outline: none;
	border: 1px solid #2563EB;
	box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.modal__input:disabled {
	background-color: #F8FAFC;
	color: #64748B;
	border-color: #E2E8F0;
}

.modal__help-text {
	font-size: 13px;
	color: #64748B;
	margin-top: 8px;
	line-height: 1.4;
}

.modal__btn {
	display: flex;
	gap: 15px;
	margin-top: 25px;
}

.modal__btn .btn {
	min-width: 160px;
	height: 55px;
	padding: 20px;
	font-weight: 600;
	border-radius: 10px;
	border: none;
	cursor: pointer;
	transition: all 0.6s;
}

.btn--white {
	background: #fff;
	color: #0F172A;
	border: 1px solid #E2E8F0;
}

.btn--white:hover {
	background: #60A5FA;
	color: #fff;
}

.btn--white:disabled {
	background: #F8FAFC;
	color: #64748B;
	cursor: not-allowed;
}

.btn--primary {
	background: #2563EB;
	color: #fff;
}

.btn--primary:hover {
	background: #60A5FA;
}

.btn--primary:disabled {
	background: #94A3B8;
	cursor: not-allowed;
}

/* Mobile responsiveness */
@media (max-width: 640px) {
	.modal__body {
		width: 90%;
		max-width: 500px;
		margin: 20px;
	}
	
	.modal__content {
		padding: 24px;
	}
	
	.modal__title {
		font-size: 20px;
	}
	
	.modal__section {
		padding: 20px;
	}
	
	.modal__btn {
		flex-direction: column;
		gap: 15px;
	}
	
	.modal__btn .btn {
		width: 100%;
		min-width: auto;
	}
}
</style>
